import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CookieService } from 'ngx-cookie-service';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { getSexEnumLabel, SexEnum } from 'src/app/_enums/sex.enum';
import {
  getVehicleDistanceLabel,
  getVehicleGarageLabel,
  getVehiclePrestationLabel,
  getVehicleRequestTypeLabel,
  getVehicleUseLabel,
  VehicleDistanceEnum,
  VehicleGarageEnum,
  VehiclePrestationsEnum,
  VehicleRequestTypeEnum,
  VehicleUseEnum,
} from 'src/app/_enums/vehicle.enums';
import { COUNTRIES, LanguageCode } from 'src/app/_helpers/nationalities';
import {
  formatDateForInput,
  getControl,
  getGroup,
} from 'src/app/_helpers/utils';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleMetadata } from 'src/app/_types/lead.types';
import { CarMaker, VehicleDetails } from 'src/app/_types/vehicle.type';

import { LeadFormBase } from '../lead-form-base';

@Component({
  selector: 'app-lead-vehicle-form',
  templateUrl: './vehicle-form.component.html',
  styleUrls: ['./vehicle-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSelectModule,
  ],
})
export class VehicleFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  private _policyHolderBirthDate!: string;

  get policyHolderBirthDate(): string {
    return this._policyHolderBirthDate;
  }

  set policyHolderBirthDate(value: string) {
    this._policyHolderBirthDate = value;
    if (value)
      (this.metadataFormGroup.controls['policyHolder'] as FormGroup).controls[
        'birthDate'
      ].setValue(new Date(value).toLocaleDateString('fr-FR'));
  }

  private _driverBirthDate!: string;

  get driverBirthDate(): string {
    return this._driverBirthDate;
  }

  set driverBirthDate(value: string) {
    this._driverBirthDate = value;
    if (value)
      (
        (this.metadataFormGroup.controls['policyHolder'] as FormGroup).controls[
          'mainDriver'
        ] as FormGroup
      ).controls['birthDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  private _firstImmatriculationDate!: string;

  get firstImmatriculationDate(): string {
    return this._firstImmatriculationDate;
  }

  set firstImmatriculationDate(value: string) {
    this._firstImmatriculationDate = value;
    if (value) {
      (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
        'firstImmatriculationDate'
      ].setValue(new Date(value).toLocaleDateString('fr-FR'));
      this.resetBrands();
      this.initBrands(this.initDone);
    }
  }

  isBrandsLoading: boolean = false;

  initDone = false;

  private _lostLicenseDate!: string | null;

  get lostLicenseDate(): string | null {
    return this._lostLicenseDate;
  }

  set lostLicenseDate(value: string | null) {
    this._lostLicenseDate = value;
    if (value)
      this.metadataFormGroup.controls['lostLicenseDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  private _differentPerson: boolean = false;

  get differentPerson(): boolean {
    return this._differentPerson;
  }

  set differentPerson(value: boolean) {
    this._differentPerson = value;
    if (value) {
      (this.metadataFormGroup.controls['policyHolder'] as FormGroup).controls[
        'mainDriver'
      ].reset();
    }
  }

  private _startDate!: string;

  get startDate(): string {
    return this._startDate;
  }

  set startDate(value: string) {
    this._startDate = value;
    if (value)
      this.metadataFormGroup.controls['insuranceStartDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  requestTypeSubscription!: Subscription;

  leasingSubscription!: Subscription;

  hasAlreadyTakenDamageSubscription!: Subscription;

  hasAlreadyLostLicenseSubscription!: Subscription;

  isAlreadyInsuredSubscription!: Subscription;

  willReplaceExistingContractSubscription!: Subscription;

  coversPrestationsSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private cookieService: CookieService,
    private vehicleInfoService: VehicleInfoService
  ) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: this.fb.control(InsuranceType.MOTOR_VEHICLE),
    requestType: this.fb.control(null),
    vehicle: this.fb.group({
      certificateId: this.fb.control(null),
      immatriculation: this.fb.control(null),
      brand: this.fb.group({
        name: this.fb.control(null),
        makeCode: this.fb.control(null),
      }),
      model: this.fb.group({
        name: this.fb.control(null),
        trimLineName: this.fb.control(null),
        modelCode: this.fb.control(null),
        modelName: this.fb.control(null),
        body: this.fb.control(null),
        transmission: this.fb.control(null),
        fuel: this.fb.control(null),
        power: this.fb.control(null),
        doors: this.fb.control(null),
        price: this.fb.control(null),
        constructionYear: this.fb.control(null),
      }),
      firstImmatriculationDate: this.fb.control(null),
    }),
    distance: this.fb.control(null),
    mainUse: this.fb.control(null),
    parkingArea: this.fb.control(null),
    garage: this.fb.control(null),
    policyHolder: this.fb.group({
      sex: this.fb.control(null),
      nationality: this.fb.control(null),
      birthDate: this.fb.control(null),
      npa: this.fb.control(null),
      licenseObtentionDate: this.fb.control(null),
      mainDriver: this.fb.group({
        sex: this.fb.control(null),
        nationality: this.fb.control(null),
        birthDate: this.fb.control(null),
        npa: this.fb.control(null),
        licenseObtentionDate: this.fb.control(null),
      }),
    }),
    leasing: this.fb.control(null),
    leasingCompany: this.fb.control(null),
    covers: this.fb.group({
      collisionDeductible: this.fb.control(null),
      partialCoverDeductible: this.fb.control(null),
      prestations: this.fb.control(null),
      personalBelongingsValue: this.fb.control(null),
    }),
    insuranceStartDate: this.fb.control(null),
    isInsured: this.fb.control(null),
    replaceContract: this.fb.control(null),
    oldCompany: this.fb.control(null),
    hasAlreadyLostLicense: this.fb.control(null),
    lostLicenseDate: this.fb.control(null),
    lostLicenseReason: this.fb.control(null),
    hasAlreadyTakenDamage: this.fb.control(null),
    damageDescription: this.fb.control(null),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const vehicleMetadata = this.metadata as VehicleMetadata;
      this.metadataFormGroup.patchValue(this.metadata);
      this.startDate = formatDateForInput(vehicleMetadata.insuranceStartDate);
      if (vehicleMetadata.lostLicenseDate)
        this.lostLicenseDate = formatDateForInput(
          new Date(vehicleMetadata.lostLicenseDate).toLocaleDateString('fr-CH')!
        );
      this.policyHolderBirthDate = formatDateForInput(
        vehicleMetadata.policyHolder.birthDate
      );
      this.firstImmatriculationDate = formatDateForInput(
        vehicleMetadata.vehicle.firstImmatriculationDate
      );
      this.metadataFormGroup.controls['vehicle'].patchValue(
        vehicleMetadata.vehicle
      );
      if (vehicleMetadata?.vehicle?.brand?.name)
        this.brandControl.setValue(vehicleMetadata.vehicle.brand.name);
      if (vehicleMetadata?.vehicle?.model?.name)
        this.modelControl.setValue(vehicleMetadata.vehicle.model.name);
      this.initModels();
      if (vehicleMetadata.policyHolder.mainDriver) {
        this.differentPerson = true;
        this.driverBirthDate = formatDateForInput(
          vehicleMetadata.policyHolder.mainDriver?.birthDate!
        );
        (this.metadataFormGroup.controls['policyHolder'] as FormGroup).controls[
          'mainDriver'
        ].patchValue(vehicleMetadata.policyHolder.mainDriver);
      }
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    this.requestTypeSubscription = this.metadataFormGroup.controls[
      'requestType'
    ].valueChanges.subscribe((value) => {
      if (value === VehicleRequestTypeEnum.NEW_IMATRICULATION) {
        (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
          'immatriculation'
        ].setValue(null);
      }
    });

    this.leasingSubscription = this.metadataFormGroup.controls[
      'leasing'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls['leasingCompany'].reset();
      }
    });

    this.hasAlreadyTakenDamageSubscription = this.metadataFormGroup.controls[
      'hasAlreadyTakenDamage'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls['damageDescription'].reset();
      }
    });

    this.hasAlreadyLostLicenseSubscription = this.metadataFormGroup.controls[
      'hasAlreadyLostLicense'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.lostLicenseDate = null;
        this.metadataFormGroup.controls['lostLicenseDate'].reset();
        this.metadataFormGroup.controls['lostLicenseReason'].reset();
      }
    });

    this.isAlreadyInsuredSubscription = this.metadataFormGroup.controls[
      'isInsured'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls['replaceContract'].reset();
      }
    });

    this.willReplaceExistingContractSubscription =
      this.metadataFormGroup.controls['replaceContract'].valueChanges.subscribe(
        (value) => {
          if (!value) {
            this.metadataFormGroup.controls['oldCompany'].reset();
          }
        }
      );

    this.coversPrestationsSubscription = (
      this.metadataFormGroup.controls['covers'] as FormGroup
    ).controls['prestations'].valueChanges.subscribe((value) => {
      this.personalBelongingsInCovers =
        value &&
        (value === VehiclePrestationsEnum.PERSONAL_BELONGINGS ||
          (Array.isArray(value) &&
            value.includes(VehiclePrestationsEnum.PERSONAL_BELONGINGS)));
    });
    this.initDone = true;
  }

  vehicleRequestTypeValues = Object.values(VehicleRequestTypeEnum);

  getVehicleRequestTypeLabel = getVehicleRequestTypeLabel;

  distanceEnumValues = Object.values(VehicleDistanceEnum);

  getDistanceEnumLabel = getVehicleDistanceLabel;

  vehicleUseValues = Object.values(VehicleUseEnum);

  getVehicleUseLabel = getVehicleUseLabel;

  garageEnumValues = Object.values(VehicleGarageEnum);

  getVehicleGarageLabel = getVehicleGarageLabel;

  countries = COUNTRIES;

  countriesISO = Object.keys(COUNTRIES);

  languageCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) ||
    ('fr' as LanguageCode);

  VehiclePrestationsEnum = VehiclePrestationsEnum;

  vehiclePrestationValues = Object.values(VehiclePrestationsEnum);

  getVehiclePrestationLabel = getVehiclePrestationLabel;

  personalBelongingsInCovers: boolean = false;

  ngOnDestroy(): void {
    if (this.requestTypeSubscription)
      this.requestTypeSubscription.unsubscribe();
    if (this.leasingSubscription) this.leasingSubscription.unsubscribe();
    if (this.coversPrestationsSubscription)
      this.coversPrestationsSubscription.unsubscribe();
    if (this.hasAlreadyTakenDamageSubscription)
      this.hasAlreadyTakenDamageSubscription.unsubscribe();
    if (this.hasAlreadyLostLicenseSubscription)
      this.hasAlreadyLostLicenseSubscription.unsubscribe();
    if (this.willReplaceExistingContractSubscription)
      this.willReplaceExistingContractSubscription.unsubscribe();
    if (this.isAlreadyInsuredSubscription)
      this.isAlreadyInsuredSubscription.unsubscribe();
  }

  genders = [SexEnum.MALE, SexEnum.FEMALE];

  getSexLabel = getSexEnumLabel;

  getGroup = getGroup;

  getControl = getControl;

  brands: CarMaker[] = [];

  filteredBrands!: Observable<CarMaker[]>;

  selectedBrand = { makeCode: 0, name: '' };

  brandControl = new FormControl({
    value: this.selectedBrand.name,
    disabled: true,
  });

  formatBrandName(brand: string) {
    const splitBrand = brand.split(' ').map((firstSplit) => {
      const splitWord = firstSplit.split('-').map((secondSplit) => {
        if (secondSplit.length > 3) return secondSplit.toLowerCase();

        return secondSplit;
      });

      return splitWord.join('-');
    });

    return splitBrand.join(' ');
  }

  onBrandChange(value: CarMaker) {
    this.selectedBrand = value;

    (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
      'brand'
    ].patchValue(value);
    this.resetModels();
    this.initModels();
  }

  initBrands(resetModels = true) {
    if (
      !this.firstImmatriculationDate ||
      new Date(this.firstImmatriculationDate).getFullYear() < 1000
    )
      return;
    this.vehicleInfoService
      .getBrands(new Date(this.firstImmatriculationDate))
      .subscribe({
        next: (res) => {
          this.brands = res.data?.carMakers || [];
          if (resetModels) {
            this.models = [];
            (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
              'model'
            ].reset();
          }
          this.filteredBrands = this.brandControl.valueChanges.pipe(
            startWith(this.selectedBrand?.name ?? ''),
            map((value) => this._filter(value || ''))
          );

          this.isBrandsLoading = false;
          this.brandControl.enable();
        },
        error: () => {
          this.isBrandsLoading = false;
        },
      });
  }

  models: VehicleDetails[] = [];

  filteredModels!: Observable<VehicleDetails[]>;

  modelControl = new FormControl({
    value: '',
    disabled: true,
  });

  isModelsLoading: boolean = false;

  initModels() {
    if (
      !(
        (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
          'brand'
        ] as FormGroup
      ).controls['makeCode'].value ||
      !this.firstImmatriculationDate
    )
      return;
    const brandName = (
      (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
        'brand'
      ] as FormGroup
    ).controls['name'].value;
    this.vehicleInfoService
      .getModels(brandName, new Date(this.firstImmatriculationDate))
      .subscribe({
        next: (res) => {
          this.models = res.data?.vehicles || [];
          this.filteredModels = this.modelControl.valueChanges.pipe(
            map((value) => this._filterModel(value || ''))
          );
          this.isModelsLoading = false;
          this.modelControl.enable();
        },
        error: () => {
          this.isModelsLoading = false;
        },
      });
  }

  onModelChange(value: VehicleDetails) {
    (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
      'model'
    ].patchValue(value.model);
  }

  private _filter(value: string): CarMaker[] {
    const filterValue = value.toLowerCase();
    return this.brands.filter((option) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  private _filterModel(value: string): VehicleDetails[] {
    const filterValue = value.toLowerCase();
    return this.models.filter(
      (option) =>
        option.model.trimLineName.toLowerCase().includes(filterValue) ||
        option.model.modelName?.toLowerCase().includes(filterValue)
    );
  }

  resetBrands() {
    this.brands = [];
    this.selectedBrand = { makeCode: 0, name: '' };
    (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
      'brand'
    ].reset();
    this.resetModels();
  }

  resetModels() {
    this.models = [];
    this.modelControl.reset();
    (this.metadataFormGroup.controls['vehicle'] as FormGroup).controls[
      'model'
    ].reset();
  }
}
